import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { addConfigApiCall } from "../../Api/api";
import apiCall from "../../Api/apiCall";

const initialState = {
   configs: {},
   revision: 0,
   loading: false,
   error: null,
};



// Async thunk to fetch all configurations and load them into Redux
export const fetchConfigsAsync = createAsyncThunk(
   "config/fetchAll",
   async (_, thunkAPI) => {
      try {
         const response = await apiCall({
            method: "GET",
            endpoint: "/configs",
            successMsg: false, // Don't show toast on initial load
         });
         
         if (response && response.data) {
            return response.data; // Expected to be an array of config objects
         }
         return [];
      } catch (error) {
         return thunkAPI.rejectWithValue("Failed to fetch configs");
      }
   }
);

// Async thunk for adding/updating a single config or multiple configs via array
export const addConfigAsync = createAsyncThunk(
   "config/add",
   async (data, thunkAPI) => {
      try {
         // handle array or single object payload
         const payloads = Array.isArray(data) ? data : [data];
         
         // Assuming API takes single object or array, we loop and send them,
         // or if the API supports array payload directly:
         // For now, let's process them iteratively if API takes single object, 
         // but we send the payload as-is if the API accepts bulk.
         // Below sends the provided payload. Adjust if API strictly requires single objects in loop.
         
         const response = await addConfigApiCall(data);
         
         if (response.data.statusCode === 200 || response.data.statusCode === 201) {
            return payloads;
         } else {
            throw response;
         }
      } catch (error) {
         const errorMessage = 
            error?.data?.message || 
            error?.response?.data?.message || 
            error?.message || 
            "Something went wrong while saving config";
         return thunkAPI.rejectWithValue(errorMessage);
      }
   }
);

const configSlice = createSlice({
   name: "config",
   initialState,
   reducers: {
      // Synchronous update in store if needed before/without API
      setConfig: (state, action) => {
         const payloads = Array.isArray(action.payload) ? action.payload : [action.payload];
         payloads.forEach(({ group, key, value }) => {
            if (!state.configs[group]) {
               state.configs[group] = {};
            }
            state.configs[group][key] = value;
         });
         state.revision += 1;
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchConfigsAsync.pending, (state) => {
            state.loading = true;
         })
         .addCase(fetchConfigsAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.revision += 1;
            const fetchedConfigs = action.payload || [];
            // Handle both flat arrays and grouped { group, configs: [...] } arrays
            fetchedConfigs.forEach((item) => {
               if (item.configs && Array.isArray(item.configs)) {
                  // Grouped structure
                  item.configs.forEach(config => {
                     if (config.key) {
                        if (!state.configs[item.group]) {
                           state.configs[item.group] = {};
                        }
                        state.configs[item.group][config.key] = config.value;
                     }
                  });
               } else if (item.group && item.key) {
                  // Flat structure
                  if (!state.configs[item.group]) {
                     state.configs[item.group] = {};
                  }
                  state.configs[item.group][item.key] = item.value;
               }
            });
         })
         .addCase(fetchConfigsAsync.rejected, (state, action) => {
            state.loading = false;
         })
         .addCase(addConfigAsync.pending, (state) => {
            state.loading = true;
            state.error = null;
         })
         .addCase(addConfigAsync.fulfilled, (state, action) => {
            state.loading = false;
            // Update the state with the successfully saved payload(s)
            const payloads = action.payload;
            payloads.forEach(({ group, key, value }) => {
               if (!state.configs[group]) {
                  state.configs[group] = {};
               }
               state.configs[group][key] = value;
            });
            state.revision += 1;
         })
         .addCase(addConfigAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
         });
   },
});

export const { setConfig } = configSlice.actions;

export const selectConfigGroup = (state, group) => state.config.configs[group] || {};
export const selectConfigValue = (state, group, key) => state.config.configs[group]?.[key];

export default configSlice.reducer;
